/**
 * Datetime display helpers for Workflow Desk.
 *
 * Backend stores UTC. Mongo/Pydantic often serialize naive datetimes as
 * "2026-07-09T11:25:00" (no Z). Browsers treat that as *local* time, so IST
 * users see the UTC wall-clock labeled as IST (e.g. 3pm IST raised → 9:30am IST).
 * Always parse naive ISO strings as UTC before formatting.
 */

let displayTimeZone = 'Asia/Kolkata';

export function getDisplayTimeZone() {
  return displayTimeZone;
}

export function setDisplayTimeZone(timeZone) {
  const next = String(timeZone || '').trim();
  if (!next) return;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: next }).format(new Date());
  } catch {
    return;
  }
  if (next === displayTimeZone) return;
  displayTimeZone = next;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('wd-timezone'));
  }
}

/** True if the string already has an explicit timezone (Z or ±HH:MM). */
function hasTimezone(s) {
  return /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
}

/**
 * Parse an API datetime as a real UTC instant.
 * Accepts Date, epoch ms, or ISO strings (with or without timezone).
 */
export function parseUtc(input) {
  if (input == null || input === '') return null;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  if (typeof input === 'number') {
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  let s = String(input).trim();
  if (!s) return null;

  // "2026-07-09 11:25:00.123000" → ISO-ish
  s = s.replace(' ', 'T');

  // Strip excessive fractional seconds Mongo may emit, keep millis
  s = s.replace(/(\.\d{3})\d+/, '$1');

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !hasTimezone(s)) {
    s = `${s}Z`;
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a UTC instant in the admin-configured display timezone.
 * Output: "dd MMM yyyy, hh:mm am/pm IST"
 */
export function formatUtcToIst(utcIsoString) {
  const date = parseUtc(utcIsoString);
  if (!date) return '—';

  const formatter = new Intl.DateTimeFormat('en-IN', {
    timeZone: displayTimeZone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });

  const parts = formatter.formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  const dayPeriod = (get('dayPeriod') || '').toLowerCase();
  const zone = get('timeZoneName');

  return `${get('day')} ${get('month')} ${get('year')}, ${get('hour')}:${get('minute')} ${dayPeriod}${zone ? ` ${zone}` : ''}`;
}

/**
 * Date-only in the display timezone (no time).
 * Output: "dd MMM yyyy"
 */
export function formatUtcToIstDate(utcIsoString) {
  const date = parseUtc(utcIsoString);
  if (!date) return '—';

  const formatter = new Intl.DateTimeFormat('en-IN', {
    timeZone: displayTimeZone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const parts = formatter.formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  return `${get('day')} ${get('month')} ${get('year')}`;
}
